import React, { useState, useEffect } from 'react';
import { HelpCircle, FileText, Search, ChevronDown, ChevronUp, Heart, Shield, Zap } from 'lucide-react';

interface FAQItem {
  category: string;
  icon: string;
  question: string;
  answer: string;
}

interface SupportProps {
  onBack: () => void;
}

const Support: React.FC<SupportProps> = ({ onBack }) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('general');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);

  useEffect(() => {
    const fetchAccountId = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;

        const apiBase = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
          ? 'http://localhost:8000/api/'
          : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

        const response = await fetch(`${apiBase}users/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.accountId) {
            setAccountId(data.accountId);
          }
        }
      } catch (error) {
        console.error('Failed to fetch account ID:', error);
      }
    };

    fetchAccountId();
  }, []);

  const supportUrl = `https://getcovered.my.site.com/forms/s/getpawtected-support?Id=${accountId}&Resident=TRUE`;

  const faqItems: FAQItem[] = [
    {
      category: 'general',
      icon: '🐕',
      question: 'How do I register my pet?',
      answer: 'Getting your furry friend registered is super easy! 🎉 Just head to your dashboard and click the "Add Pet" button. Our step-by-step wizard will guide you through entering your pet\'s details, uploading documents, and completing the registration. The whole process takes about 5-10 minutes!'
    },
    {
      category: 'sa',
      icon: '🦮',
      question: 'What qualifies a pet as a Service Animal?',
      answer: 'A Service Animal is individually trained to do work or perform tasks for a person with a disability. Unlike Emotional Support Animals, Service Animals must be task-trained. Common examples include guiding individuals who are blind, alerting individuals who are deaf, pulling a wheelchair, or alerting and protecting a person who is having a seizure.'
    },
    {
      category: 'sa',
      icon: '🪪',
      question: 'Do I need documentation for a Service Animal?',
      answer: 'Properties may not require documentation that discloses disability. However, they may ask if the animal is required because of a disability and what tasks the animal has been trained to perform. No pet rent or deposits are typically charged for Service Animals.'
    },
    {
      category: 'sa',
      icon: '📄',
      question: 'How do I register a Service Animal in GetPawtected?',
      answer: 'Select Service Animal during pet setup. Provide basic info about training and tasks performed. You can optionally upload training documentation if you have it, though it is not required by law.'
    },
    {
      category: 'general',
      icon: '📋',
      question: 'What documents do I need for registration?',
      answer: 'You\'ll need: ✅ Current vaccination records (especially rabies!) ✅ Health certificate from your vet ✅ Any medical records or prescriptions ✅ Emotional Support Animal letter (if applicable) ✅ Pet insurance documents (if you have them). Don\'t worry - you can upload photos or PDFs of these documents!'
    },
    {
      category: 'general',
      icon: '💰',
      question: 'How much does pet registration cost?',
      answer: 'Pet registration is $25 per pet for Standard Pets. There is no cost for Emotional Support Animal (ESA) or Service Animal registrations.'
    },
    {
      category: 'general',
      icon: '⏰',
      question: 'How long does the approval process take?',
      answer: 'Most pet registrations are approved within 24-48 hours! ⚡ Our team reviews your documents quickly, and you\'ll get an email notification once your pet is approved. For Emotional Support Animals, the process might take an extra day for additional verification.'
    },
    {
      category: 'general',
      icon: '🏠',
      question: 'Can I register multiple pets?',
      answer: 'Absolutely! 🐕🐱 You can register as many pets as your lease allows. Each pet goes through the same simple registration process. Pro tip: You can register them all at once using our multi-pet workflow!'
    },
    {
      category: 'technical',
      icon: '🔐',
      question: 'I can\'t access my account',
      answer: 'No worries, this happens! 😅 First, try clearing your browser cache and cookies, then attempt to log in again. Make sure you\'re using the correct email address. If you\'re still stuck, click "Forgot Password" or contact our support team - we\'re here to help!'
    },
    {
      category: 'technical',
      icon: '📱',
      question: 'Is the platform mobile-friendly?',
      answer: 'Yes! 📲 GetPawtected works perfectly on your phone, tablet, or computer. You can register pets, upload documents, and check your pet\'s status from anywhere. The mobile experience is optimized for easy document uploads using your phone\'s camera!'
    },
    {
      category: 'technical',
      icon: '🔄',
      question: 'My document upload failed. What should I do?',
      answer: 'Document upload issues are usually quick fixes! 🛠️ Try these steps: ✅ Check your internet connection ✅ Make sure your file is under 10MB ✅ Use JPG, PNG, or PDF formats ✅ Try refreshing the page and uploading again. Still having trouble? Our support team can help!'
    },
    {
      category: 'esa',
      icon: '🏥',
      question: 'What makes a pet an Emotional Support Animal?',
      answer: 'An Emotional Support Animal is prescribed by a licensed mental health professional! 👩‍⚕️ You\'ll need a valid Emotional Support Animal letter from a psychiatrist, psychologist, or licensed therapist. This letter must be current (within the last year) and specifically recommend an Emotional Support Animal for your mental health.'
    },
    {
      category: 'esa',
      icon: '📄',
      question: 'What should be included in my Emotional Support Animal letter?',
      answer: 'Your Emotional Support Animal letter should include: ✅ Licensed professional\'s letterhead ✅ Their license number and signature ✅ Date of issuance (within last 12 months) ✅ Statement of your mental health condition ✅ Recommendation for Emotional Support Animal ✅ Professional\'s contact information. We verify all Emotional Support Animal letters for authenticity!'
    },
    {
      category: 'esa',
      icon: '🏡',
      question: 'Do Emotional Support Animals pay pet rent or deposits?',
      answer: 'Good news! 🎉 Emotional Support Animals are typically exempt from pet rent and deposits under fair housing laws. However, you\'re still responsible for any damage your Emotional Support Animal might cause. Each property may have different policies, so check with your property manager for specifics.'
    }
  ];

  const filteredFAQs = faqItems.filter(item => 
    item.category === selectedCategory && 
    item.question.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  const handleStartLiveChat = () => {
    // Add live chat functionality here
    console.log('Starting live chat...');
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, React.ReactElement> = {
      general: <Heart className="w-4 h-4" />,
      technical: <Zap className="w-4 h-4" />,
      esa: <Shield className="w-4 h-4" />,
      sa: <Shield className="w-4 h-4" />
    };
    return icons[category] || <HelpCircle className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-[#f9f9fb]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div className="bg-white border-b border-[#cdced7] h-[73px] flex items-center">
        <div className="px-4 sm:px-5 w-full flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#1e1f24]">Help & Support</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative w-full px-1 py-5">
        <div className="px-4 sm:px-5 w-full flex items-center justify-between">
          {/* Responsive Grid: stacks on mobile, 2 columns on lg */}
          <div className="grid gap-5 [grid-template-columns:1fr] lg:[grid-template-columns:250px_1fr]">
            {/* Contact Section */}
            <div>
              <h3 className="text-xl leading-[120%] text-black font-inter mb-3">
                Get in Touch
              </h3>

              {/* Support CTA */}
              <div className="mt-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] rounded-[5px] bg-white border border-[#cdced7] overflow-hidden">
                <div className="border-b border-[#cdced7] flex flex-col p-[15px] space-y-[5px]">
                  <div className="flex items-center">
                    <div className="flex-1 flex items-center">
                      <div className="flex-1 relative leading-6 text-[#1e1f24] font-inter font-semibold">
                        Still need help?
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="relative leading-[140%] text-[#62636c] font-inter text-sm">
                      If you couldn't find the answer you were looking for, our support team is here to help.
                    </div>
                  </div>
                  <a 
                    href={supportUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center shadow-[0px_1px_2px_rgba(0,0,0,0.05)] rounded-[1000px] bg-white border border-[#b9bbc6] h-8 overflow-hidden px-[10px] py-2 text-[#1e1f24] hover:bg-gray-50 transition-colors w-max mt-2"
                  >
                    <FileText className="w-3 h-3 mr-2" />
                    <span className="relative leading-5 uppercase font-medium text-xs font-inter">
                      Click for Support
                    </span>
                  </a>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="space-y-[20px]">
              <h2 className="text-xl leading-[120%] text-black font-inter">
                Frequently Asked Questions
              </h2>
            {/* Search and Filter */}
              <div className="shadow-[0px_1px_2px_rgba(0,0,0,0.05)] rounded-[5px] bg-white border border-[#cdced7] w-full overflow-hidden">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between p-[15px]">
                  <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'general', label: 'General' },
                    { key: 'technical', label: 'Technical' },
                    { key: 'esa', label: 'Emotional Support Animal' },
                    { key: 'sa', label: 'Service Animal' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setSelectedCategory(key)}
                      className={`px-3 py-1 rounded-[1000px] text-xs font-medium transition-colors ${
                        selectedCategory === key
                          ? 'text-white border-0 shadow bg-clip-padding focus:ring-0'
                          : 'bg-white text-[color:var(--pmc-primary)] border-[0.5px] border-[#b9bbc6] hover:bg-gray-50 focus:ring-2 focus:ring-[color:var(--pmc-primary)]/30'
                      } focus:outline-none`}
                      style={selectedCategory === key ? { background: 'var(--pmc-primary)' } : undefined}
                    >
                      {label}
                    </button>
                  ))}
                  </div>
                  <div className="relative w-full md:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#62636c]" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search questions..."
                    className="w-full pl-9 pr-3 py-2 text-sm rounded border border-[#cdced7] focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-primary)]/30 focus:border-[color:var(--pmc-primary)] bg-white text-[#1e1f24] placeholder-[#9a9cab]"
                  />
                  </div>
                </div>
              </div>

            {/* FAQ Accordions */}
            {filteredFAQs.map((item, index) => (
              <div
                key={index}
                className="shadow-[0px_1px_2px_rgba(0,0,0,0.05)] rounded-[5px] bg-white border border-[#cdced7] w-full overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between text-left px-[15px] py-[14px] hover:bg-[#f9f9fb]"
                  aria-expanded={expandedFAQ === index}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-[color:var(--pmc-primary)]">
                      {getCategoryIcon(item.category)}
                    </div>
                    <span className="leading-6 text-[#1e1f24] font-inter">
                      {item.question}
                    </span>
                  </div>
                  <div className="text-[#62636c]">
                    {expandedFAQ === index ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>
                </button>
                {expandedFAQ === index && (
                  <div className="border-t border-[#cdced7] px-[15px] pb-[14px] pt-3">
                    <div className="leading-[140%] text-[#62636c] font-inter text-sm">
                      {item.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Support;
